import { client } from '../client';
import { UserRoleName } from './types/UserProfile';

export interface NetworkOption {
  code: string;
  label: string;
  role: string;
}

/**
 * Lists the D1 Network catalog (Admin/SuperAdmin only).
 *
 * - Super Admin → GET /superadmin/networks
 * - Admin (and everyone else who can reach this) → GET /admin/networks
 */
export const listNetworkOptions = async (userRole?: UserRoleName): Promise<NetworkOption[]> => {
  const prefix = userRole === UserRoleName.SuperAdmin ? '/superadmin' : '/admin';
  const { data } = await client.get<NetworkOption[]>(`${prefix}/networks`);

  return data;
};

/** Narrows the network catalog to the option(s) valid for a given user role (e.g. 'Provider', 'CareAdvisor'). */
export const getNetworkOptionsForRole = (catalog: NetworkOption[], role: string): NetworkOption[] =>
  catalog.filter((option) => option.role === role);

/**
 * Static fallback labels for the two known network codes, for read-only display on a Provider's or
 * CareAdvisor's own profile. The catalog endpoint (`listNetworkOptions`) is Admin/SuperAdmin-only, so a
 * Provider/CareAdvisor viewing their own profile can't fetch it to resolve their assigned code to a label.
 */
export const NETWORK_LABELS: Record<string, string> = {
  d1_provider_network: 'D1 Provider Network',
  d1_coordinator_network: 'D1 Coordinator Network',
};

export const getNetworkLabel = (code: string | null | undefined): string => (code ? NETWORK_LABELS[code] || code : '');

/**
 * A Client Admin may see a Provider/Care Advisor who carries a D1 Network marker (they can be
 * assigned to the client's cases), but must not manage their profile: no editing details, no
 * deactivating, no account-status changes. Full management stays with D1 Admins/Super Admins.
 */
export const isNetworkManagedByD1 = (
  actorRole: string | undefined,
  targetNetwork: string | null | undefined,
): boolean => actorRole === UserRoleName.OrganizationAdmin && Boolean(targetNetwork);
